/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useState, useEffect, useMemo } from "react";
import DataGridButton from "../../components/DataGridButton";
import {
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Switch,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { UserContext } from "../../contexts";
import { tokens } from "../../theme";
import useResizableDrawerWidth from "../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../components/DrawerSnapToggle";
import DetailPanelContent from "./DetailPanelContent";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import Notification from "../../components/Notification";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import FilterMultiSelect from "../../components/FilterMultiSelect";
import {
  useGetVulnerabilities,
  useGetAllVulnerabilityFilterOptions,
  usePatchVulnerability,
} from "../../api/vulnerabilities";
import {
  createQueryString,
  handleAdminister,
  handleExcludeForAll,
  handleRequestApproval,
  updateSelectedItemsInFilter,
} from "./utils";
import { adaptVulnerabilities } from "./adapter";
import { showNotification, toTitleCase } from "../../utils/helperFunctions";
import { USER_ROLES, VULNERABILITY_STATUS } from "../../utils/constants";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import {
  closeButtonStyles,
  dataGridButtonStyles,
} from "../../styles/buttonStyles";
import { tabbedDataGridPage } from "../../styles/commonPageStyles";
import {
  packageVulnerabilityStyles as styles,
  vulnerabilityDrawerStyles as drawerStyles,
} from "./styles";
import { ChevronRight, Close, Done, Sync } from "@mui/icons-material";

const PackageVulnerabilities = ({ vulnerabilityType, header }) => {
  const loggedInUser = useContext(UserContext);

  const theme = useTheme();
  const colors = tokens(theme.paletteId);

  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [totalFindings, setTotalFindings] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedItemsInFilter, setSelectedItemsInFilter] = useState({});
  const [isUserCodeVulnerabilityReviewer, setIsUserCodeVulnerabilityReviewer] =
    useState(false);
  const [detailsRow, setDetailsRow] = useState(null);
  const closeDetailsDrawer = () => setDetailsRow(null);
  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: detailsDrawerWidth,
    isWide: isDetailsDrawerWide,
    toggleWide: toggleDetailsDrawerWide,
    handleProps: detailsResizeProps,
  } = useResizableDrawerWidth({
    storageKey: "vulnerability-detail",
    defaultWidth: 720,
    maxWidth: 1200,
    enabled: isDesktopDrawer,
  });

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: "",
    content: "",
    handleDialogConfirm: () => {},
  });

  const resetDialog = () => {
    setConfirmationDialog({
      isOpen: false,
      title: "",
      content: "",
      handleDialogConfirm: () => {},
    });
  };

  const {
    postData: getVulnerabilities,
    response: getVulnerabilitiesResp,
    isLoading: isLoadingVulnerabilities,
  } = useGetVulnerabilities(vulnerabilityType);

  const {
    postData: patchVulnerability,
    response: patchVulnerabilityResp,
    responseStatus: patchVulnerabilityStatus,
  } = usePatchVulnerability();

  const {
    postData: getAllVulnerabilityFilterOptions,
    response: getAllVulnerabilityFilterOptionsResp,
  } = useGetAllVulnerabilityFilterOptions(vulnerabilityType);

  useEffect(() => {
    getAllVulnerabilityFilterOptions();
  }, []);
  useEffect(() => {
    setIsUserCodeVulnerabilityReviewer(
      loggedInUser?.data?.roles?.includes(
        USER_ROLES.CODE_VULNERABILITY_REVIEWERS
      )
    );
  }, [loggedInUser?.data]);

  const hasActiveFilters = Object.values(selectedItemsInFilter).some(
    (values) => values?.length
  );

  useEffect(() => {
    if (Array.isArray(getVulnerabilitiesResp?.response)) {
      const rows = adaptVulnerabilities(getVulnerabilitiesResp.response);
      // adaptVulnerabilities re-derives status (excludedForAll → APPROVED), so the
      // server-side status filter can let rows through whose displayed status no
      // longer matches the selection. Re-apply it on the derived status.
      const statusKey = Object.keys(selectedItemsInFilter).find(
        (key) => key.toLowerCase() === "status"
      );
      const selectedStatuses =
        (statusKey && selectedItemsInFilter[statusKey]) || [];
      const filteredRows = selectedStatuses.length
        ? rows.filter((row) => selectedStatuses.includes(row?.status))
        : rows;
      setVulnerabilities(filteredRows);
      if (!hasActiveFilters) setTotalFindings(rows.length);
    }
  }, [getVulnerabilitiesResp]);

  // Severity + findings count per vulnerability ID, shown in the filter
  // popover rows. Built from the currently loaded rows.
  const vulnerabilityMeta = useMemo(() => {
    return vulnerabilities.reduce((meta, row) => {
      const id = row?.vulnerabilityId;
      if (!id) return meta;
      const severity = row?.metaData?.Severity || row?.metaData?.level;
      meta[id] = {
        severity: meta[id]?.severity || severity,
        count: (meta[id]?.count || 0) + 1,
      };
      return meta;
    }, {});
  }, [vulnerabilities]);

  useEffect(() => {
    if (getAllVulnerabilityFilterOptionsResp?.response) {
      setFilters(getAllVulnerabilityFilterOptionsResp.response);
    }
  }, [getAllVulnerabilityFilterOptionsResp]);

  useEffect(() => {
    getVulnerabilities(createQueryString(selectedItemsInFilter));
  }, [selectedItemsInFilter]);

  // Notifications
  const [notify, setNotify] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("");

  const notificationCallback = () => {
    getVulnerabilities(createQueryString(selectedItemsInFilter));
    getAllVulnerabilityFilterOptions();
  };
  const showNotificationWrapper = (resp, respStatus, callback = () => {}) => {
    showNotification(
      resp,
      respStatus,
      callback,
      setMessage,
      setSeverity,
      setNotify
    );
  };

  useEffect(() => {
    showNotificationWrapper(
      patchVulnerabilityResp,
      patchVulnerabilityStatus,
      notificationCallback
    );
  }, [patchVulnerabilityResp, patchVulnerabilityStatus]);

  const columns = [
    {
      field: "details",
      width: 44,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => "",
      renderCell: ({ row }) => (
        <IconButton size="small" onClick={() => setDetailsRow(row)}>
          <ChevronRight fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "repository",
      renderHeader: () => "Repository Name",
      flex: 2,
      cellClassName: "name-column--cell",
      renderCell: ({ row: { repository } }) => repository || "",
    },
    {
      field: "vulnerabilityId",
      flex: 1.75,
      renderHeader: () => "Vulnerability ID",
      renderCell: ({ row: { vulnerabilityId } }) => vulnerabilityId ?? "",
    },
    {
      field: "packageName",
      flex: 1.75,
      renderHeader: () => "Package",
      renderCell: ({ row: { packageName } }) => packageName ?? "",
    },
    {
      field: "status",
      flex: 1.5,
      renderHeader: () => "Status",
      renderCell: ({ row: { status } }) => <StatusChip value={status} />,
    },
    {
      field: "excludedForAll",
      flex: 1,
      renderHeader: () => "Exclude All",
      renderCell: ({ row }) => (
        <Switch
          checked={row?.excludedForAll}
          onChange={() =>
            handleExcludeForAll(row, setConfirmationDialog, patchVulnerability)
          }
          size="small"
          disabled={!isUserCodeVulnerabilityReviewer}
        />
      ),
    },
    {
      field: "fixAvailable",
      flex: 1,
      renderHeader: () => "Fix Available",
      renderCell: ({ row: { fixAvailable } }) =>
        fixAvailable ? (
          <Done sx={styles.fixAvailableIconStyles(colors, fixAvailable)} />
        ) : (
          <Close sx={styles.fixAvailableIconStyles(colors, fixAvailable)} />
        ),
    },
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: ({ row }) =>
        [
          isUserCodeVulnerabilityReviewer &&
            row.status !== VULNERABILITY_STATUS.APPROVED && (
              <GridActionsCellItem
                label="Approve"
                onClick={() =>
                  handleAdminister(
                    row,
                    "Approve",
                    setConfirmationDialog,
                    patchVulnerability
                  )
                }
                showInMenu
              />
            ),
          isUserCodeVulnerabilityReviewer &&
            row.status !== VULNERABILITY_STATUS.REJECTED && (
              <GridActionsCellItem
                label="Reject"
                onClick={() =>
                  handleAdminister(
                    row,
                    "Reject",
                    setConfirmationDialog,
                    patchVulnerability
                  )
                }
                showInMenu
              />
            ),
          [VULNERABILITY_STATUS.NEW, VULNERABILITY_STATUS.REJECTED].includes(
            row?.status
          ) && (
            <GridActionsCellItem
              label="Request Approval"
              onClick={() =>
                handleRequestApproval(
                  row,
                  setConfirmationDialog,
                  patchVulnerability
                )
              }
              showInMenu
            />
          ),
        ].filter(Boolean),
    },
  ].filter(Boolean);

  return (
    <Box sx={styles.mainBox}>
      <Notification
        notify={notify}
        severity={severity}
        setNotify={setNotify}
        message={message}
      />
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        title={confirmationDialog.title}
        handleDialogConfirm={confirmationDialog.handleDialogConfirm}
        content={confirmationDialog.content}
        resetDialog={resetDialog}
      />
      <Grid sx={styles.headerGrid} container columns={2}>
        <Grid xs={1}>{header}</Grid>
        <Grid xs={1}>
          <Box sx={tabbedDataGridPage.topButtonStack}>
            <DataGridButton
              sx={dataGridButtonStyles.pageTopButtons}
              onClick={() =>
                getVulnerabilities(createQueryString(selectedItemsInFilter))
              }
            >
              <Sync sx={{ fontSize: 16, color: "#fff" }} />
              <Typography sx={dataGridButtonTypography(colors, 500)}>
                RELOAD
              </Typography>
            </DataGridButton>
          </Box>
        </Grid>
      </Grid>
      <Box sx={styles.filterStack}>
        {Object.keys(filters).map((filterName) => (
          <FilterMultiSelect
            key={filterName}
            label={toTitleCase(filterName)}
            options={filters[filterName].map((item) => item.toString())}
            selected={selectedItemsInFilter[filterName] || []}
            onChange={updateSelectedItemsInFilter(
              setSelectedItemsInFilter,
              filterName
            )}
            itemsNoun={/vulnerability/i.test(filterName) ? "IDs" : "options"}
            getOptionMeta={
              /vulnerability/i.test(filterName)
                ? (option) => vulnerabilityMeta[option]
                : undefined
            }
            sx={styles.filterButton(filterName)}
          />
        ))}
        <Typography sx={styles.findingsCount}>
          {totalFindings != null
            ? `${vulnerabilities.length} of ${totalFindings} findings`
            : ""}
        </Typography>
      </Box>
      {hasActiveFilters ? (
        <Box sx={styles.appliedFiltersRow}>
          <Typography sx={styles.appliedFiltersLabel}>FILTERING BY</Typography>
          {Object.keys(selectedItemsInFilter).flatMap((filterName) =>
            (selectedItemsInFilter[filterName] || []).map((value) => (
              <Chip
                key={`${filterName}-${value}`}
                label={value}
                size="small"
                sx={styles.appliedFilterChip(colors)}
                onDelete={() =>
                  updateSelectedItemsInFilter(
                    setSelectedItemsInFilter,
                    filterName
                  )(
                    selectedItemsInFilter[filterName].filter(
                      (item) => item !== value
                    )
                  )
                }
              />
            ))
          )}
          <Button
            sx={styles.clearAllButton}
            onClick={() => setSelectedItemsInFilter({})}
          >
            Clear all
          </Button>
        </Box>
      ) : null}
      <DataTable
        height="100%"
        sx={styles.dataTable}
        getRowId={(row) => row?.id}
        loading={isLoadingVulnerabilities}
        rows={vulnerabilities}
        columns={columns}
      />
      <Drawer
        anchor="right"
        open={Boolean(detailsRow)}
        onClose={closeDetailsDrawer}
        PaperProps={{
          sx: {
            ...drawerStyles.paper(colors, detailsDrawerWidth),
            overflow: "visible",
          },
        }}
      >
        <DrawerResizeHandle
          {...detailsResizeProps}
          ariaLabel="Resize vulnerability detail drawer"
        />
        <DrawerSnapToggle
          enabled={isDesktopDrawer}
          isWide={isDetailsDrawerWide}
          onClick={toggleDetailsDrawerWide}
          ariaLabel="Expand vulnerability detail drawer to 75% width"
          collapseLabel="Collapse vulnerability detail drawer"
          sx={{ position: "absolute", left: "-12px", top: "12px", zIndex: 9 }}
        />
        {detailsRow ? (
          <>
            <Box sx={drawerStyles.header(colors)}>
              <Box>
                <Typography sx={drawerStyles.title(colors)}>
                  {detailsRow?.vulnerabilityId}
                </Typography>
                <Typography sx={drawerStyles.subtitle(colors)}>
                  {[detailsRow?.packageName, detailsRow?.repository]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
              <IconButton
                sx={closeButtonStyles(colors)}
                onClick={closeDetailsDrawer}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={drawerStyles.body}>
              <DetailPanelContent row={detailsRow} />
            </Box>
          </>
        ) : null}
      </Drawer>
    </Box>
  );
};

export default PackageVulnerabilities;
